import Swup from 'swup';
import SwupBodyClassPlugin from '@swup/body-class-plugin';
import SwupHeadPlugin from '@swup/head-plugin';
import SwupJsPlugin from '@swup/js-plugin';
import SwupPreloadPlugin from '@swup/preload-plugin';
import { getThemeColor } from './page-theme.js';
import { closeNavIfScheduled } from './cmn.js';

if (!window.__swupMinimalLoaded) {
  window.__swupMinimalLoaded = true;
  console.log('vite main loaded');
}

var origin = window.location.origin;
var linkSelector = 'a[href^="/"]:not([target="_blank"]):not([data-no-swup]):not([download])' +
  ':not([href^="#"]):not([href^="mailto:"]):not([href^="tel:"])' +
  ':not([href*="/wp-admin"]):not([href*="/wp-login.php"]), ' +
  'a[href^="' + origin + '"]:not([target="_blank"]):not([data-no-swup]):not([download])' +
  ':not([href^="#"]):not([href^="mailto:"]):not([href^="tel:"])' +
  ':not([href*="/wp-admin"]):not([href*="/wp-login.php"])';

var EXPAND_MS = 800;
var FADE_OUT_MS = 450;

function isSameOrigin(link) {
  return link.origin === window.location.origin;
}

function isContactPath(pathname) {
  return pathname === '/contact' || pathname.indexOf('/contact/') === 0;
}

var contactNavInProgress = false;

function isExcludedNavigationLink(link) {
  if (!link || link.tagName !== 'A') {
    return true;
  }
  if (!link.href) {
    return true;
  }
  if (!isSameOrigin(link)) {
    return true;
  }
  if (link.target === '_blank') {
    return true;
  }
  if (link.hasAttribute('data-no-swup')) {
    return true;
  }
  if (link.hasAttribute('download')) {
    return true;
  }
  if (link.getAttribute('href') && link.getAttribute('href').charAt(0) === '#') {
    return true;
  }
  if (link.href.indexOf('mailto:') === 0 || link.href.indexOf('tel:') === 0) {
    return true;
  }
  if (link.href.indexOf('/wp-admin') !== -1 || link.href.indexOf('/wp-login.php') !== -1) {
    return true;
  }
  return false;
}

function shouldHandleLink(link) {
  if (isExcludedNavigationLink(link)) {
    return false;
  }
  if (isContactPath(link.pathname)) {
    return false;
  }
  return true;
}

function shouldAnimateContactNavigation(link) {
  if (isExcludedNavigationLink(link)) {
    return false;
  }
  return isContactPath(link.pathname);
}

function navigateToContactWithTransition(link) {
  if (contactNavInProgress) {
    return;
  }

  contactNavInProgress = true;
  var href = link.href;

  runTransitionOut({
    visit: {
      to: {
        url: href,
        pathname: link.pathname
      }
    }
  }).then(function () {
    window.location.assign(href);
  }).catch(function () {
    contactNavInProgress = false;
  });
}

function prefersReducedMotion() {
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

function initPage() {
  if (!window.__swupMinimalGuardInstalled) {
    window.__swupMinimalGuardInstalled = true;
    document.addEventListener('click', function (event) {
      var link = event.target.closest('a');
      if (!link) {
        return;
      }

      // フォーム送信の都合で Swup 対象外の contact は、遷移アニメ後に通常遷移
      if (shouldAnimateContactNavigation(link)) {
        event.preventDefault();
        event.stopImmediatePropagation();
        navigateToContactWithTransition(link);
        return;
      }

      // data-no-swup など Swup 対象外リンクは伝播を止めない（works ページャー等の独自ハンドラ用）
    }, true);
  }
}

function resolvePathname(urlOrPath) {
  if (!urlOrPath) {
    return window.location.pathname;
  }

  try {
    return new URL(urlOrPath, window.location.origin).pathname;
  } catch (error) {
    return urlOrPath;
  }
}

function getDestinationPathname(data) {
  if (!data) {
    return window.location.pathname;
  }

  if (data.visit && data.visit.to) {
    return resolvePathname(data.visit.to.url || data.visit.to.pathname);
  }

  if (data.to && data.to.url) {
    return resolvePathname(data.to.url);
  }

  if (data.to && data.to.pathname) {
    return resolvePathname(data.to.pathname);
  }

  return window.location.pathname;
}

function wait(ms) {
  return new Promise(function (resolve) {
    window.setTimeout(resolve, ms);
  });
}

function waitForAnimation(el, fallbackMs) {
  if (!el) {
    return wait(fallbackMs);
  }

  return new Promise(function (resolve) {
    var done = false;
    var finish = function () {
      if (done) {
        return;
      }
      done = true;
      el.removeEventListener('animationend', onEnd);
      resolve();
    };
    var onEnd = function (event) {
      if (event.target === el) {
        finish();
      }
    };

    el.addEventListener('animationend', onEnd);
    window.setTimeout(finish, fallbackMs + 80);
  });
}

function getTransitionLayer() {
  var layer = document.getElementById('swup-transition-layer');
  if (layer) {
    return layer;
  }

  layer = document.createElement('div');
  layer.id = 'swup-transition-layer';
  layer.setAttribute('aria-hidden', 'true');

  var circle = document.createElement('span');
  circle.className = 'transition-anime';
  layer.appendChild(circle);

  document.body.appendChild(layer);
  return layer;
}

function getCircle(layer) {
  return layer.querySelector('.transition-anime');
}

function resetCircleClasses(circle) {
  if (!circle) {
    return;
  }
  circle.classList.remove('is-expand', 'is-fade-out');
  circle.style.animation = 'none';
  void circle.offsetWidth;
  circle.style.animation = '';
}

function runTransitionOut(data) {
  lockScrollPosition();

  if (prefersReducedMotion()) {
    closeNavIfScheduled();
    return Promise.resolve();
  }

  var layer = getTransitionLayer();
  var circle = getCircle(layer);
  var color = getThemeColor(getDestinationPathname(data));

  layer.style.setProperty('--swup-transition-color', color);
  resetCircleClasses(circle);

  layer.classList.add('is-visible');

  return wait(16).then(function () {
    if (circle) {
      circle.classList.add('is-expand');
    }
    return waitForAnimation(circle, EXPAND_MS);
  }).then(function () {
    closeNavIfScheduled();
  });
}

function waitForPaint() {
  return new Promise(function (resolve) {
    requestAnimationFrame(function () {
      requestAnimationFrame(resolve);
    });
  });
}

function showMainContent(main) {
  if (!main) {
    return;
  }
  main.style.opacity = '';
  main.style.visibility = '';
  main.style.transform = '';
}

function runTransitionIn() {
  var layer = getTransitionLayer();
  var circle = getCircle(layer);
  var main = document.querySelector('#swup');

  showMainContent(main);

  if (prefersReducedMotion()) {
    layer.classList.remove('is-visible');
    resetCircleClasses(circle);
    return Promise.resolve();
  }

  // コンテンツを描画してから円をフェードアウト
  return waitForPaint().then(function () {
    if (circle) {
      circle.classList.add('is-fade-out');
    }
    return waitForAnimation(circle, FADE_OUT_MS);
  }).then(function () {
    layer.classList.remove('is-visible');
    resetCircleClasses(circle);
  });
}

var scrollLockY = 0;
var isScrollLocked = false;

function lockScrollPosition() {
  if (isScrollLocked) {
    return;
  }

  scrollLockY = window.scrollY || window.pageYOffset || 0;
  isScrollLocked = true;
  document.body.style.position = 'fixed';
  document.body.style.top = '-' + scrollLockY + 'px';
  document.body.style.left = '0';
  document.body.style.right = '0';
  document.body.style.width = '100%';
}

function unlockScrollPosition() {
  if (!isScrollLocked) {
    return;
  }

  document.body.style.position = '';
  document.body.style.top = '';
  document.body.style.left = '';
  document.body.style.right = '';
  document.body.style.width = '';
  isScrollLocked = false;
}

function resetScrollAfterNavigation() {
  var scrollingElement = document.scrollingElement || document.documentElement;

  unlockScrollPosition();
  scrollingElement.scrollLeft = 0;
  scrollingElement.scrollTop = 0;

  if (typeof window.scrollTo === 'function') {
    try {
      window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
    } catch (error) {
      window.scrollTo(0, 0);
    }
  }

  document.documentElement.scrollTop = 0;
  document.body.scrollTop = 0;
}

if ('scrollRestoration' in history) {
  history.scrollRestoration = 'manual';
}

if (!window.__swupMinimalInstance) {
  window.__swupMinimalInstance = new Swup({
    containers: ['#swup'],
    linkSelector: linkSelector,
    animationSelector: '.swup-transition',
    ignoreVisit: function (url) {
      var parsedUrl = new URL(url, window.location.origin);
      return isContactPath(parsedUrl.pathname);
    },
    plugins: [
      new SwupBodyClassPlugin(),
      new SwupHeadPlugin(),
      new SwupJsPlugin({
        animations: [
          {
            from: '(.*)',
            to: '(.*)',
            out: function (data) {
              return runTransitionOut(data);
            },
            in: function () {
              return runTransitionIn();
            }
          }
        ]
      }),
      new SwupPreloadPlugin()
    ]
  });

  console.log('swup initialized');

  window.__swupMinimalInstance.hooks.on('content:replace', function () {
    resetScrollAfterNavigation();
    console.log('swup content replaced');
    initPage();
    document.dispatchEvent(new CustomEvent('swup:replaced'));
    console.log('page view', window.location.pathname);
    if (typeof window.gtag === 'function') {
      window.gtag('event', 'page_view', {
        page_path: window.location.pathname + window.location.search,
        page_location: window.location.href,
        page_title: document.title
      });
    }
  });
}

initPage();
